import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import axios from 'axios';

import { Box, Typography } from '@mui/material';
import VideoJSComponent from './components/VideoJSComponent';
import RecordListOfDescription from './components/RecordListOfDescriptions';
import VideoRecordInformation from './components/VideoRecordInformation';
import { useAppSelector, useAppDispatch } from '../../redux';
import { userSelectors } from '../../redux/user';
import { ScheduleDialog } from '../../components';
import SelectionButtons from './components/SelectionButtons';
import RecordActionsButtons from './components/RecordActionsButtons';
import { ExitInterviewDialog } from './components/ExitInterviewDialog';
import RecordInputsButtons from './components/RecordInputsButtons';
import FinishInterviewLayout from './components/FinishInterviewLayout';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  useIsIndependentVIPage,
  useIsMobileBrowser,
  useNetworkStatus,
  useUserVideoInterview,
} from '../../hooks';
import { LocalStorageService, logEvent } from '../../services';
import { LOCAL_STORAGE_KEYS, WORK_AREA_TYPE } from '../../constants';
import {
  finishVideoInterviewQuestion,
  finishVideoInterview,
  getVideoInterviewUploadUrl,
} from '../../redux/videoInterview';
import { notificationAlert } from 'src/redux/notifications';
import {
  IVideoInterviewQuestion,
  VideoInterviewQuestionStatus,
} from '../../types/videoInterviewQuestion';
import { UserVideoInterviewStatus } from 'src/types/userVideoInterviewData';
import ShareFeedbackDialog from 'src/features/shareFeedbackDialog/ShareFeedbackDialog';
import RetakeConfirmationDialog from './components/RetakeConfirmationDialog';
import VideoErrorBoundaryWithFeedback from 'src/components/VideoErrorBoundaryWithFeedback/VideoErrorBoundaryWithFeedback';

// Minimum acceptable recording size in bytes (~10KB — anything smaller is almost certainly corrupt)
const MIN_RECORDING_SIZE_BYTES = 10000;

// Timeout for ts-ebml conversion before falling back to raw data
const CONVERT_TIMEOUT_MS = 30000;

// Helper to validate audio stream health before recording
const validateAudioStream = (
  stream: MediaStream | null,
): { isValid: boolean; error?: string } => {
  if (!stream) {
    return { isValid: false, error: 'No media stream available' };
  }

  const audioTracks = stream.getAudioTracks();
  if (audioTracks.length === 0) {
    return { isValid: false, error: 'No audio tracks found' };
  }

  const track = audioTracks[0];

  if (track.readyState === 'ended') {
    return { isValid: false, error: 'Audio track has ended unexpectedly' };
  }

  if (!track.enabled) {
    return { isValid: false, error: 'Audio track is disabled' };
  }

  if (track.muted) {
    return { isValid: false, error: 'Audio track is muted' };
  }

  return { isValid: true };
};

// Helper to validate video stream health before recording
const validateVideoStream = (
  stream: MediaStream | null,
): { isValid: boolean; error?: string } => {
  if (!stream) {
    return { isValid: false, error: 'No media stream available' };
  }

  const videoTracks = stream.getVideoTracks();
  if (videoTracks.length === 0) {
    return { isValid: false, error: 'No video tracks found' };
  }

  const track = videoTracks[0];

  if (track.readyState === 'ended') {
    return { isValid: false, error: 'Video track has ended unexpectedly' };
  }

  if (!track.enabled) {
    return { isValid: false, error: 'Video track is disabled' };
  }

  return { isValid: true };
};

// Helper to validate a recorded blob before upload
const validateRecordingBlob = (
  blob: Blob | null,
): { isValid: boolean; error?: string } => {
  if (!blob) {
    return { isValid: false, error: 'No recording data available' };
  }

  if (blob.size === 0) {
    return { isValid: false, error: 'Recording is empty (0 bytes)' };
  }

  if (blob.size < MIN_RECORDING_SIZE_BYTES) {
    return {
      isValid: false,
      error: `Recording is too small (${blob.size} bytes) and likely corrupted`,
    };
  }

  if (!blob.type) {
    return { isValid: false, error: 'Recording has no MIME type' };
  }

  return { isValid: true };
};

const RecordVideo: React.FC = () => {
  const dispatch = useAppDispatch();
  const playerRef = useRef(null);
  const audioTrackListenersRef = useRef<(() => void) | null>(null);
  const videoTrackListenersRef = useRef<(() => void) | null>(null);
  const convertTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFinishingInterviewRef = useRef(false);
  // Recording stream as STATE (not ref) so child components re-render when it changes
  // (e.g., when user switches audio input device mid-session)
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(
    null,
  );
  const isMobile = useIsMobileBrowser();
  const { isSlowConnection } = useNetworkStatus();

  const {
    specializationType,
    isLoading,
    userVideoInterview,
    allQuestionsAmount,
    finishedQuestionsAmount,
    currentQuestionNumber,
    currentQuestionAttempts,
    isLastQuestion,
  } = useUserVideoInterview();
  const userData = useAppSelector(userSelectors.getUserData)!;
  const isIndependentVIPage = useIsIndependentVIPage();
  const userReminderDate = userData?.videoInterviewReminderDate;

  const [deviceIsBlocked, setDeviceIsBlocked] = useState(false);
  const [deviceIsReady, setDeviceIsReady] = useState(false);
  const [openReportIssue, setOpenReportIssue] = useState(false);
  const [showRetakeDialog, setShowRetakeDialog] = useState(false);
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [videoInputDevices, setVideoInputDevices] = useState([]);
  const [currentAudioInput, setCurrentAudioInput] = useState('');
  const [currentVideoInput, setCurrentVideoInput] = useState('');
  const [showRescheduleMessage, setShowRescheduleMessage] = useState(
    Boolean(userReminderDate),
  );
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showExitInterviewDialog, setShowExitInterviewDialog] = useState(false);
  const [isStartRecord, setIsStartRecord] = useState(false);
  const [currentQuestion, setCurrentQuestion] =
    useState<IVideoInterviewQuestion | null>(
      userVideoInterview?.videoInterview?.questions.find(
        (vi) => vi.questionNumber === currentQuestionNumber + 1,
      ) || null,
    );
  const [questionStatus, setQuestionStatus] =
    useState<VideoInterviewQuestionStatus>(
      VideoInterviewQuestionStatus.PREPARATION,
    );
  const [recordData, setRecordData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const isEngineeringSpecialization = useMemo(
    () => specializationType === WORK_AREA_TYPE.ENGINEERING,
    [specializationType],
  );

  const nonTechInformBar =
    specializationType === WORK_AREA_TYPE.NON_ENGINEERING &&
    deviceIsReady &&
    !isStartRecord;

  const finishRecordQuestion = () => {
    const player = playerRef.current as any;
    if (player?.player_) {
      player.record().stop();
    }
  };

  // [FIX #9] Validate both audio and video streams before starting recording.
  // On failure, reset status to PREPARATION so the UI doesn't get stuck.
  const startRecordQuestion = useCallback(() => {
    const player = playerRef.current as any;
    if (!player?.player_) return;

    const stream = player.record().stream;

    // Validate audio stream
    const audioValidation = validateAudioStream(stream);
    if (!audioValidation.isValid) {
      console.error('Audio validation failed:', audioValidation.error);
      dispatch(
        notificationAlert(
          `Audio issue detected: ${audioValidation.error}. Please check your microphone and refresh the page.`,
          { variant: 'error' },
        ),
      );
      // [FIX #9] Reset to PREPARATION so the user can retry instead of getting stuck
      setQuestionStatus(VideoInterviewQuestionStatus.PREPARATION);
      try {
        player.record().getDevice();
      } catch (err) {
        console.error('Failed to re-acquire device:', err);
      }
      return;
    }

    // Validate video stream
    const videoValidation = validateVideoStream(stream);
    if (!videoValidation.isValid) {
      console.error('Video validation failed:', videoValidation.error);
      dispatch(
        notificationAlert(
          `Video issue detected: ${videoValidation.error}. Please check your camera and refresh the page.`,
          { variant: 'error' },
        ),
      );
      setQuestionStatus(VideoInterviewQuestionStatus.PREPARATION);
      try {
        player.record().getDevice();
      } catch (err) {
        console.error('Failed to re-acquire device:', err);
      }
      return;
    }

    player.record().start();
  }, [dispatch]);

  const resetRecordQuestion = () => {
    const player = playerRef.current as any;
    if (player?.player_) {
      player.record().reset();
    }
  };

  // [FIX #12] Do NOT call destroy() immediately after stop().
  // stop() triggers async data flushing; destroying before it completes loses the recording.
  // The player will be cleaned up when the component unmounts via disposePlayer().
  const handleFinishVideoInterview = () => {
    if (!userVideoInterview) return;

    isFinishingInterviewRef.current = true;
    finishRecordQuestion();
    // Removed: destroyRecordQuestion() — let finishRecord event fire first

    dispatch(
      finishVideoInterview({
        userVideoInterviewId: userVideoInterview.id,
      }),
    );

    LocalStorageService.clearState(
      LOCAL_STORAGE_KEYS.START_VIDEO_INTERVIEW_RECORD,
    );
  };

  useEffect(() => {
    logEvent('launchpod - video-interview-record-page_loaded');

    if (userVideoInterview && finishedQuestionsAmount === allQuestionsAmount) {
      setQuestionStatus(VideoInterviewQuestionStatus.STOPPED);
    }

    // [FIX #6] Removed: mobile pre-permission request.
    // Opening a getUserMedia stream and immediately stopping it can interfere with
    // the actual recording stream on mobile browsers (especially iOS Safari).
    // Let videojs-record's getDevice() handle the single permission request.

    // Listen for device changes (mic plugged/unplugged) and re-enumerate
    const handleDeviceChange = () => {
      const player = playerRef.current as any;
      if (player?.player_ && !player.isDisposed()) {
        player.record().enumerateDevices();
        // Re-validate the current stream — the active device may have been removed
        const stream = player.record().stream;
        if (stream) {
          const audioTracks = stream.getAudioTracks();
          if (
            audioTracks.length === 0 ||
            audioTracks[0].readyState === 'ended'
          ) {
            dispatch(
              notificationAlert(
                'Microphone disconnected. Please select a different microphone or reconnect your device.',
                { variant: 'error' },
              ),
            );
          }
        }
      }
    };

    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener(
        'devicechange',
        handleDeviceChange,
      );
    }

    // Cleanup on unmount
    return () => {
      if (navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          handleDeviceChange,
        );
      }
      if (audioTrackListenersRef.current) {
        audioTrackListenersRef.current();
        audioTrackListenersRef.current = null;
      }
      if (videoTrackListenersRef.current) {
        videoTrackListenersRef.current();
        videoTrackListenersRef.current = null;
      }
      if (convertTimeoutRef.current) {
        clearTimeout(convertTimeoutRef.current);
        convertTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    window.onbeforeunload = (e) => {
      e.preventDefault();
      if (
        questionStatus === VideoInterviewQuestionStatus.STARTED ||
        questionStatus === VideoInterviewQuestionStatus.LOADING ||
        questionStatus === VideoInterviewQuestionStatus.STOPPING
      ) {
        return true;
      }
    };

    window.onunload = () => {
      finishRecordQuestion();
    };

    if (
      LocalStorageService.getState(
        LOCAL_STORAGE_KEYS.START_VIDEO_INTERVIEW_RECORD,
      )
    ) {
      setIsStartRecord(true);
    }
  }, [questionStatus]);

  useEffect(() => {
    if (
      questionStatus === VideoInterviewQuestionStatus.STARTED &&
      userVideoInterview &&
      currentQuestion
    ) {
      startRecordQuestion();
    } else if (questionStatus === VideoInterviewQuestionStatus.STOPPING) {
      finishRecordQuestion();
    }
  }, [questionStatus]);

  useEffect(() => {
    const handleQuestionUpload = async () => {
      if (recordData) {
        // [FIX #10] Validate the recorded blob before uploading
        const blobValidation = validateRecordingBlob(recordData);
        if (!blobValidation.isValid) {
          console.error('Recording validation failed:', blobValidation.error);
          dispatch(
            notificationAlert(
              `Recording failed: ${blobValidation.error}. Please try recording again.`,
              { variant: 'error' },
            ),
          );
          setQuestionStatus(VideoInterviewQuestionStatus.PREPARATION);
          setRecordData(null);
          return;
        }

        const response = await dispatch(
          getVideoInterviewUploadUrl({
            userVideoInterviewId: userVideoInterview!.id,
            questionNumber: currentQuestion!.questionNumber,
            record: recordData,
          }),
        );
        if (getVideoInterviewUploadUrl.fulfilled.match(response)) {
          try {
            const presignedPostUrl = response.payload.url;
            const presignedPostFields = response.payload.fields;
            const formData = new FormData();

            // Add all required fields from the presigned post
            Object.entries(presignedPostFields).forEach(([key, value]) => {
              formData.append(key, value);
            });

            // Then append the file last
            formData.append('file', recordData);

            // Show a notification for mobile users that upload is in progress
            if (isMobile) {
              dispatch(
                notificationAlert('Uploading video, please keep the app open', {
                  variant: 'info',
                }),
              );
            }

            if (isSlowConnection) {
              dispatch(
                notificationAlert(
                  'Slow internet connection detected. Upload might take longer.',
                  { variant: 'warning', autoHideDuration: 10000 },
                ),
              );
            }

            // [FIX #1] Do NOT set Content-Type header manually for presigned POST uploads.
            // FormData automatically sets Content-Type to multipart/form-data with the correct
            // boundary. Setting it to the video MIME type causes S3 to receive a malformed
            // multipart request, resulting in corrupted files with missing audio/video.
            await axios.post(presignedPostUrl, formData, {
              onUploadProgress: (progressEvent) => {
                const percent = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total,
                );
                onUploadProgress(percent);
              },
            });

            setUploadProgress(0);

            await dispatch(
              finishVideoInterviewQuestion({
                userVideoInterviewId: userVideoInterview!.id,
                questionNumber: currentQuestion!.questionNumber,
                fileName: response.payload.fileName,
              }),
            );

            setQuestionStatus(VideoInterviewQuestionStatus.STOPPED);

            // Success notification for mobile
            if (isMobile) {
              dispatch(
                notificationAlert('Upload complete!', {
                  variant: 'success',
                  autoHideDuration: 3000,
                }),
              );
            }
          } catch (err) {
            console.error('Upload failed', err);
            dispatch(
              notificationAlert('Failed uploading video. Please try again.', {
                variant: 'error',
              }),
            );

            // On mobile, show a more detailed error message
            if (isMobile) {
              dispatch(
                notificationAlert(
                  "If you're on a mobile connection, please ensure you have a stable internet connection",
                  {
                    variant: 'warning',
                  },
                ),
              );
            }
          }
        }
      }
    };

    handleQuestionUpload();
  }, [recordData]);

  useEffect(() => {
    const nextQuestion = userVideoInterview?.videoInterview?.questions.find(
      (vi) => vi.questionNumber === currentQuestionNumber + 1,
    );

    if (
      questionStatus === VideoInterviewQuestionStatus.FINISHED &&
      !isLoading
    ) {
      setQuestionStatus(VideoInterviewQuestionStatus.PREPARATION);
      setCurrentQuestion(nextQuestion || null);
      setRecordData(null);
      resetRecordQuestion();
    }
  }, [questionStatus, isLoading, userVideoInterview]);

  const handleScheduleSuccess = () => {
    setShowRescheduleMessage(true);
    setShowScheduleDialog(false);
  };

  const handleSelectAudioInput = (event: SelectChangeEvent) => {
    setCurrentAudioInput(event.target.value as string);
    if (playerRef && playerRef.current) {
      (playerRef.current as any).record().setAudioInput(event.target.value);
    }
  };

  const handleSelectVideoInput = (event: SelectChangeEvent) => {
    setCurrentVideoInput(event.target.value as string);

    if (playerRef && playerRef.current) {
      (playerRef.current as any).record().setVideoInput(event.target.value);
    }
  };

  const handleStartRecordSuccess = (e: React.SyntheticEvent | null): void => {
    if (e) {
      e.preventDefault();
    }

    logEvent('launchpod - video-interview-record-page_start-interview');

    LocalStorageService.setState(
      LOCAL_STORAGE_KEYS.START_VIDEO_INTERVIEW_RECORD,
      true,
    );
    setIsStartRecord(true);
  };

  // [FIX #5] handleBrowserSettings is the SOLE place that calls getDevice().
  // The duplicate getDevice() call has been removed from the 'ready' event handler.
  const handleBrowserSettings = async (player: any) => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permissionName = 'camera' as PermissionName;
        const settings = await navigator.permissions.query({
          name: permissionName,
        });

        const handlePermissionChange = () => {
          // Check permission state without opening a competing stream.
          // On supported browsers, use the Permissions API result directly.
          // Only fall back to getUserMedia if we need to trigger the prompt.
          if (settings.state === 'granted') {
            player.record().getDevice();
            setDeviceIsBlocked(false);
          } else if (settings.state === 'prompt') {
            // Need to trigger permission prompt — let getDevice() handle it
            // so there's only ONE getUserMedia call
            player.record().getDevice();
          } else {
            setDeviceIsReady(false);
            setDeviceIsBlocked(true);
          }
        };

        settings.addEventListener('change', handlePermissionChange, {
          once: false,
        });

        // Initial check
        handlePermissionChange();
      } else {
        // Fallback for browsers that don't support the Permissions API.
        // Let getDevice() make the single getUserMedia call.
        player.record().getDevice();
      }
    } catch (error) {
      console.error('Permission error:', error);
      // Fallback: try getDevice() directly
      try {
        player.record().getDevice();
      } catch (deviceErr: unknown) {
        console.error('getDevice fallback failed:', deviceErr);
        // Only block for genuine permission denials — TypeError means a Video.js
        // internal UI init error (device button element missing), not a camera block.
        if (
          (deviceErr as { name?: string })?.name === 'NotAllowedError' ||
          (deviceErr as { name?: string })?.name === 'PermissionDeniedError'
        ) {
          setDeviceIsBlocked(true);
        }
      }
    }
  };

  const handlePlayerReady = (player: any) => {
    playerRef.current = player;

    // [FIX #5] Only call handleBrowserSettings which handles getDevice() internally.
    // Previously, getDevice() was also called directly here, creating a race condition
    // where two competing getUserMedia requests could cause the stream's video track
    // to become inactive (black video).
    player.on('ready', function () {
      handleBrowserSettings(player);
    });

    player.on('deviceReady', () => {
      const stream = player.record().stream;

      if (!stream) {
        console.error('Stream not available after device ready');
        setDeviceIsBlocked(true);
        return;
      }

      // Store stream in state so SoundMeter and ActiveMicIndicator re-render when it changes
      setRecordingStream(stream);

      const hasVideo = stream.getVideoTracks().length > 0;
      const hasAudio = stream.getAudioTracks().length > 0;

      if (!hasVideo) {
        dispatch(
          notificationAlert(
            'Video input not available. Please enable camera permissions and refresh the page.',
            { variant: 'error' },
          ),
        );
        return;
      }

      if (!hasAudio) {
        dispatch(
          notificationAlert(
            'Audio input not available. Please enable microphone permissions and refresh the page.',
            { variant: 'error' },
          ),
        );
        return;
      }

      // Set up audio track lifecycle monitoring
      const audioTrack = stream.getAudioTracks()[0];

      // Clean up previous listeners if any
      if (audioTrackListenersRef.current) {
        audioTrackListenersRef.current();
      }

      const handleAudioTrackEnded = () => {
        console.error('Audio track ended unexpectedly');
        dispatch(
          notificationAlert(
            'Audio input disconnected. Please check your microphone and refresh the page.',
            { variant: 'error' },
          ),
        );
      };

      const handleAudioTrackMute = () => {
        console.warn('Audio track muted');
        dispatch(
          notificationAlert(
            'Microphone muted. Please unmute your microphone to continue recording.',
            { variant: 'warning' },
          ),
        );
      };

      audioTrack.addEventListener('ended', handleAudioTrackEnded);
      audioTrack.addEventListener('mute', handleAudioTrackMute);

      audioTrackListenersRef.current = () => {
        audioTrack.removeEventListener('ended', handleAudioTrackEnded);
        audioTrack.removeEventListener('mute', handleAudioTrackMute);
      };

      // Set up video track lifecycle monitoring
      const videoTrack = stream.getVideoTracks()[0];

      if (videoTrackListenersRef.current) {
        videoTrackListenersRef.current();
      }

      const handleVideoTrackEnded = () => {
        console.error('Video track ended unexpectedly');
        dispatch(
          notificationAlert(
            'Camera disconnected. Please check your camera and refresh the page.',
            { variant: 'error' },
          ),
        );
      };

      videoTrack.addEventListener('ended', handleVideoTrackEnded);

      videoTrackListenersRef.current = () => {
        videoTrack.removeEventListener('ended', handleVideoTrackEnded);
      };

      setDeviceIsBlocked(false);
      setDeviceIsReady(true);
      player.record().enumerateDevices();
    });

    player.on('enumerateReady', () => {
      const devices = player.record().devices;
      const audioInputs = devices.filter(
        ({ kind }: { kind: string }) => kind === 'audioinput',
      );
      const videoInputs = devices.filter(
        ({ kind }: { kind: string }) => kind === 'videoinput',
      );

      setAudioInputDevices(audioInputs);
      setVideoInputDevices(videoInputs);

      // For mobile, prefer front camera if available
      if (isMobile && videoInputs.length > 0) {
        const frontCamera = videoInputs.find((device: any) =>
          device.label.toLowerCase().includes('front'),
        );
        if (frontCamera) {
          setCurrentVideoInput(frontCamera.deviceId);
        } else {
          setCurrentVideoInput(videoInputs[0].deviceId);
        }
      } else {
        videoInputs.length && setCurrentVideoInput(videoInputs[0].deviceId);
      }

      audioInputs.length && setCurrentAudioInput(audioInputs[0].deviceId);
    });

    // [FIX #4 + #8] Simplified and bulletproofed finishRecord/finishConvert logic.
    // Previously, a fragile mimeType check could cause neither handler to save data.
    // Now: if convertEngine is set, we wait for finishConvert with a timeout fallback.
    // If no convertEngine, we save immediately from finishRecord.
    player.on('finishRecord', () => {
      const hasConvertEngine = !!player.record().convertEngine;

      if (hasConvertEngine) {
        // [FIX #8] Set a safety timeout — if ts-ebml conversion doesn't complete
        // within CONVERT_TIMEOUT_MS, fall back to the raw recording data.
        // This prevents data loss when ts-ebml fails silently or runs out of memory.
        convertTimeoutRef.current = setTimeout(() => {
          console.warn(
            'ts-ebml conversion timed out, using raw recording data',
          );
          const data = player.recordedData;
          const validation = validateRecordingBlob(data);
          if (validation.isValid) {
            setRecordData(data);
            player.controls(true);
          } else {
            console.error('Raw recording data also invalid:', validation.error);
            dispatch(
              notificationAlert('Recording failed. Please try again.', {
                variant: 'error',
              }),
            );
            setQuestionStatus(VideoInterviewQuestionStatus.PREPARATION);
          }
        }, CONVERT_TIMEOUT_MS);
        return;
      }

      // No convert engine — save raw data immediately
      const data = player.recordedData;
      const validation = validateRecordingBlob(data);
      if (validation.isValid) {
        setRecordData(data);
        player.controls(true);
      } else {
        console.error('Recording validation failed:', validation.error);
        dispatch(
          notificationAlert(
            'Recording appears to be empty or corrupted. Please try again.',
            { variant: 'error' },
          ),
        );
        setQuestionStatus(VideoInterviewQuestionStatus.PREPARATION);
      }
    });

    player.on('finishConvert', () => {
      // Clear the conversion timeout since we got the event
      if (convertTimeoutRef.current) {
        clearTimeout(convertTimeoutRef.current);
        convertTimeoutRef.current = null;
      }

      try {
        const data = player.convertedData;
        const validation = validateRecordingBlob(data);
        if (validation.isValid) {
          setRecordData(data);
          player.controls(true);
        } else {
          // Converted data is bad, try raw data as fallback
          console.warn('Converted data invalid, falling back to raw data');
          const rawData = player.recordedData;
          const rawValidation = validateRecordingBlob(rawData);
          if (rawValidation.isValid) {
            setRecordData(rawData);
            player.controls(true);
          } else {
            console.error('Both converted and raw data are invalid');
            dispatch(
              notificationAlert('Recording failed. Please try again.', {
                variant: 'error',
              }),
            );
            setQuestionStatus(VideoInterviewQuestionStatus.PREPARATION);
          }
        }
      } catch (err) {
        console.error('Error processing converted data:', err);
        // Fallback to raw recording data
        const rawData = player.recordedData;
        const rawValidation = validateRecordingBlob(rawData);
        if (rawValidation.isValid) {
          setRecordData(rawData);
          player.controls(true);
        } else {
          dispatch(
            notificationAlert('Recording failed. Please try again.', {
              variant: 'error',
            }),
          );
          setQuestionStatus(VideoInterviewQuestionStatus.PREPARATION);
        }
      }
    });

    player.on('deviceError', (error: any) => {
      console.error('Device error:', error);
      setDeviceIsBlocked(true);
      dispatch(
        notificationAlert(
          'Camera or microphone error. Please check your device permissions and refresh the page.',
          { variant: 'error' },
        ),
      );
    });

    player.on('error', (error: any) => {
      console.error('Player error:', error);
      dispatch(
        notificationAlert(
          'Video recording error. Please try again or use a different browser.',
          { variant: 'error' },
        ),
      );
    });
  };

  const handleRetakeQuestion = () => {
    let question = userVideoInterview?.videoInterview?.questions.find(
      (vi) => vi.questionNumber === currentQuestion?.questionNumber,
    );

    if (question && question.preparation) {
      question = JSON.parse(JSON.stringify(question));
      question!.preparation = 0.2;
    }

    setShowRetakeDialog(false);
    setUploadProgress(0);
    setQuestionStatus(VideoInterviewQuestionStatus.LOADING);
    setCurrentQuestion(question || null);
    setRecordData(null);
    resetRecordQuestion();
  };

  const onUploadProgress = (percents: number) => {
    setUploadProgress(percents);
  };

  const isIndependentVideoInterviewFinished = useMemo(() => {
    return (
      userVideoInterview?.status === UserVideoInterviewStatus.FINISHED &&
      isIndependentVIPage
    );
  }, [userVideoInterview, isIndependentVIPage]);

  if (isIndependentVideoInterviewFinished) {
    return <FinishInterviewLayout />;
  }

  return (
    <>
      {showScheduleDialog && (
        <ScheduleDialog
          isOpen={showScheduleDialog}
          handleClose={() => setShowScheduleDialog(false)}
          handleScheduleSuccess={handleScheduleSuccess}
          isVideoInterview={true}
        />
      )}
      {showExitInterviewDialog && (
        <ExitInterviewDialog
          isOpen={showExitInterviewDialog}
          handleClose={() => setShowExitInterviewDialog(false)}
          handleFinishVideoInterview={handleFinishVideoInterview}
        />
      )}
      <Box
        sx={(theme) => ({
          [theme.breakpoints.down('md')]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          },
        })}
      >
        <Typography
          variant="h2"
          mb={3}
          sx={(theme) => ({
            color: isStartRecord ? 'text.secondary' : 'text.primary',
            [theme.breakpoints.down('md')]: {
              display: 'none',
            },
          })}
        >
          {isStartRecord
            ? 'Tell us about yourself'
            : 'Prepare your camera and mic'}
        </Typography>
        <Box
          sx={(theme) => ({
            display: 'flex',
            width: '511px',
            [theme.breakpoints.down('xl')]: {
              flexDirection: 'column',
              alignItems: isStartRecord ? 'inherit' : 'center',
            },
            ['@media (max-width: 1100px)']: {
              width: '100%',
            },
          })}
        >
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              [theme.breakpoints.down('xl')]: {
                width: '100%',
              },
            })}
          >
            <VideoJSComponent
              onReady={handlePlayerReady}
              deviceIsReady={deviceIsReady}
              deviceIsBlocked={deviceIsBlocked}
              duration={currentQuestion?.duration || 0}
              questionStatus={questionStatus}
              recordingStream={recordingStream}
            />
          </Box>
          {!isStartRecord && (
            <Typography
              variant="h2"
              mb={3}
              sx={(theme) => ({
                display: 'none',
                [theme.breakpoints.down('md')]: {
                  display: 'flex',
                  mt: '20px',
                },
              })}
            >
              Ready?
            </Typography>
          )}
          {nonTechInformBar && (
            <Box
              sx={(theme) => ({
                background: '#F4FBFE',
                height: '281px',
                borderRadius: '8px',
                padding: '0 55px',
                display: 'flex',
                alignItems: 'center',
                [theme.breakpoints.down('xl')]: {
                  display: 'none',
                },
                [theme.breakpoints.up('xl')]: {
                  minWidth: '350px',
                  justifyContent: 'center',
                  maxWidth: '400px',
                },
                [theme.breakpoints.up('xxl')]: {
                  padding: '0 55px',
                  maxWidth: '550px',
                },
              })}
            >
              <RecordListOfDescription />
            </Box>
          )}
          {isStartRecord && currentQuestion && (
            <VideoRecordInformation
              currentQuestion={currentQuestion}
              questionsAmount={allQuestionsAmount}
              setQuestionStatus={setQuestionStatus}
              questionStatus={questionStatus}
              recordingStream={recordingStream}
            />
          )}
        </Box>
        {!isStartRecord && (
          <RecordInputsButtons
            videoInputDevices={videoInputDevices}
            handleSelectVideoInput={handleSelectVideoInput}
            currentVideoInput={currentVideoInput}
            audioInputDevices={audioInputDevices}
            handleSelectAudioInput={handleSelectAudioInput}
            currentAudioInput={currentAudioInput}
            deviceIsBlocked={deviceIsBlocked}
          />
        )}
        {nonTechInformBar && (
          <Box
            sx={(theme) => ({
              display: 'none',
              marginTop: '20px',
              [theme.breakpoints.down('xl')]: {
                display: 'flex',
              },
              [theme.breakpoints.down('md')]: {
                display: 'none',
              },
            })}
          >
            <RecordListOfDescription />
          </Box>
        )}
        {isStartRecord ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <RecordActionsButtons
              questionStatus={questionStatus}
              setQuestionStatus={setQuestionStatus}
              setShowExitInterviewDialog={setShowExitInterviewDialog}
              isLastQuestion={isLastQuestion}
              handleFinishVideoInterview={handleFinishVideoInterview}
              showReportIssueDialog={() => setOpenReportIssue(true)}
              attempts={currentQuestionAttempts}
              handleShowRetakeDialog={() => setShowRetakeDialog(true)}
              uploadProgress={uploadProgress}
              isEngineeringSpecialization={isEngineeringSpecialization}
              isDisabled={isLoading}
            />
          </Box>
        ) : (
          <SelectionButtons
            deviceIsReady={deviceIsReady}
            setShowScheduleDialog={setShowScheduleDialog}
            showRescheduleMessage={showRescheduleMessage}
            setShowRescheduleMessage={setShowRescheduleMessage}
            handleStartRecordSuccess={handleStartRecordSuccess}
            questionNumber={currentQuestion?.questionNumber || 0 + 1}
          />
        )}
        {nonTechInformBar && (
          <Box
            sx={(theme) => ({
              display: 'none',
              marginTop: '20px',
              [theme.breakpoints.down('md')]: {
                display: 'flex',
                maxWidth: '300px',
                margin: '40px auto',
              },
            })}
          >
            <RecordListOfDescription />
          </Box>
        )}

        {/* Mobile-specific help text */}
        {isMobile && !deviceIsReady && (
          <Box sx={{ mt: 2, textAlign: 'center', px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              For best results, please use your device in portrait orientation
              and ensure you have a stable internet connection.
            </Typography>
          </Box>
        )}
      </Box>
      <ShareFeedbackDialog
        isOpen={openReportIssue}
        handleClose={() => setOpenReportIssue(false)}
      />
      <RetakeConfirmationDialog
        isOpen={showRetakeDialog}
        handleClose={() => setShowRetakeDialog(false)}
        handleRetakeQuestion={handleRetakeQuestion}
      />
    </>
  );
};

const RecordVideoWithErrorBoundary: React.FC = () => {
  return (
    <VideoErrorBoundaryWithFeedback
      fallbackTitle="Video Interview Error"
      fallbackMessage="We encountered an issue with the video interview system. This could be due to browser compatibility, network connectivity, or device access problems. Please try refreshing the page or contact support if the issue persists."
      onRetry={() => window.location.reload()}
    >
      <RecordVideo />
    </VideoErrorBoundaryWithFeedback>
  );
};

export default RecordVideoWithErrorBoundary;
